import React, { useContext, useMemo, useCallback } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  BidStrategyComponent,
  BID_STRATEGY_OPTIONS_MAP,
  MAX_CONVERSIONS,
  MAX_CONVERSIONS_VALUE
} from '@bingads-webui-campaign-react/bid-strategy-component';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import {
  TextTitle,
  ValidatedPanel,
  getPanelErrorProps,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import {
  entityIs,
  propertyIs,
  andComp,
} from '@bingads-webui-campaign-react/form-error';
import {
  withUnvalidatedControlErrors,
  withControlErrorHandling,
} from '@bingads-webui-campaign-react/error-handling';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import { TargetCpaWarningBanner } from '@bingads-webui-campaign-react/target-cpa-warning-banner';

import { ConversionBasedBiddingBanner } from '../conversion-based-bidding-banner';
import { BidStrategyImg } from '../../media';
import { ERROR_ENTITY } from '../../constants';

const getBidStrategyStyles = () => ({
  budgetTitle: {
    fontSize: '18px',
    display: 'inline',
  },
  bottomMargin: {
    marginBottom: '8px',
  },
});

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const bidStrategyProp = andComp(entityIsCampaign, propertyIs('BiddingScheme'));
const ServerValidatedBidStrategyComponent = withControlErrorHandling(withUnvalidatedControlErrors(BidStrategyComponent, { filter: bidStrategyProp }));

export const BidStrategyPanel = (props) => {
  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext) || {};
  const serverContext = useContext(ServerContext);
  const { CurrentAccount: { CurrencyCode } } = serverContext || {};
  const bidRange = (serverContext && serverContext.KeywordBidRange) || null;

  const getStyles = useResponsiveStyles(getBidStrategyStyles);

  const {
    key,
    isLoading,
    expand,
    onDataChange,
    options,
    data = {},
    topicKey,
    ...rest
  } = props;

  const {
    campaign,
    conversionGoalDetails,
    bidStrategy,
    currency,
    cpa,
    roas,
    isInitialAdditionalDetailsChecked,
    budgetAmount,
  } = data;

  const {
    bidStrategyComponentOptions = {},
    biddingBannerOptions = {},
    scenario = '',
  } = options;

  const {
    bidStrategyOptions,
    hideAdditionalDetails,
    ...restBidStrategyComponentOptions
  } = bidStrategyComponentOptions;

  const {
    ...restBiddingBannerOptions
  } = biddingBannerOptions;

  const activity = useContext(ActivityContext);

  const bidStrategyTitleDetails = useMemo(() => {
    let targetValue = '';
    let bidStrategyText = '';
    if (bidStrategy) {
      if (bidStrategy === MAX_CONVERSIONS.type && cpa) {
        targetValue = `${cpa} ${currency}`;
      } else if (bidStrategy === MAX_CONVERSIONS_VALUE.type && roas) {
        targetValue = `${roas} %`;
      }
      const predefinedBidStrategy = BID_STRATEGY_OPTIONS_MAP[bidStrategy];
      bidStrategyText = predefinedBidStrategy ? predefinedBidStrategy.text : bidStrategy;
    }

    return (
      <>
        {i18n.getString(_TL_('{{bidStrategyText}}'), { bidStrategyText }) + (targetValue ? `, ${i18n.getString(_TL_('target {{targetValue}}'), { targetValue })}` : '')}
        <TargetCpaWarningBanner
          budgetAmount={budgetAmount}
          cpa={cpa}
          activity={activity}
          scenario={scenario}
        />
      </>
    );
  }, [bidStrategy, i18n, budgetAmount, cpa, activity, scenario, roas, currency]);

  const addBottomMargin = useMemo(() => hideAdditionalDetails, [hideAdditionalDetails]);

  const onFieldChange = useCallback((updatedData) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        ...updatedData,
      }),
    });
  }, [onDataChange]);

  const onBidStrategyChange = useCallback((e, newBidStrategy) => {
    const bidStrategyType = _.get(newBidStrategy, 'type');
    onFieldChange({ bidStrategy: bidStrategyType });
  }, [onFieldChange]);

  const onMarketingMetricChange = useCallback(({
    data: newMarketingMetric,
    isValid,
    messages,
  }) => {
    onFieldChange({
      ...newMarketingMetric,
      isMarketingMetricValid: isValid,
      marketingMetricErrors: messages,
    });
  }, [onFieldChange]);

  return (
    <ValidatedPanel
      key={key}
      title={
        <TextTitle
          icon={BidStrategyImg}
          titleKey={_TL_('Bid strategy')}
          topicKey={topicKey || 'pop_BA_PMAX_BidStrategy'}
          as="h2"
          headingClassName={classNames(getStyles('budgetTitle'))}
          details={bidStrategyTitleDetails}
        />
      }
      actuationProps={{
        expandKey: key,
        expand,
      }}
      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      {...rest}
    >
      <ServerValidatedBidStrategyComponent
        options={bidStrategyOptions}
        selectedBidStrategy={bidStrategy}
        onBidStrategyChange={onBidStrategyChange}
        defaultChecked={isInitialAdditionalDetailsChecked}
        marketingMetricsValue={{ cpa, roas }}
        onMarketingMetricChange={onMarketingMetricChange}
        inputUnit={CurrencyCode}
        bidRange={bidRange}
        isLoading={isLoading}
        hideAdditionalDetails={hideAdditionalDetails}
        hideMessage
        {...restBidStrategyComponentOptions}
      />
      <TargetCpaWarningBanner
        budgetAmount={budgetAmount}
        cpa={cpa}
        activity={activity}
        scenario={scenario}
      />
      <ConversionBasedBiddingBanner
        i18n={i18n}
        scope={scope}
        campaign={campaign}
        conversionGoalsDetails={conversionGoalDetails}
        {...restBiddingBannerOptions}
      />
      {addBottomMargin && <div className={classNames(getStyles('bottomMargin'))} />}
    </ValidatedPanel>
  );
};

BidStrategyPanel.propTypes = {
  key: PropTypes.string.isRequired,
  onDataChange: PropTypes.func.isRequired,
  data: PropTypes.shape({
    bidStrategy: PropTypes.string,
    currency: PropTypes.string,
    cpa: PropTypes.number,
    roas: PropTypes.number,
    campaign: PropTypes.shape({}),
    conversionGoalDetails: PropTypes.shape({}),
    isInitialAdditionalDetailsChecked: PropTypes.bool,
  }),
  options: PropTypes.shape({
    bidStrategyComponentOptions: PropTypes.shape({}),
    biddingBannerOptions: PropTypes.shape({}),
    scenario: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
  expand: PropTypes.func.isRequired,
  topicKey: PropTypes.string,
};

BidStrategyPanel.defaultProps = {
  data: {},
  options: {},
  isLoading: false,
  topicKey: null,
};
